import React from 'react';
import { useLanguageSwitcher } from '@/shared/hooks/useLocalization';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'flags' | 'minimal';
  showFlags?: boolean;
  className?: string;
}

const LANGUAGE_FLAGS: Record<string, string> = {
  pt: '🇧🇷',
  'pt-pt': '🇵🇹',
  en: '🇺🇸',
  es: '🇪🇸',
  ja: '🇯🇵',
  ru: '🇷🇺',
};

const LANGUAGE_DISPLAY: Record<string, string> = {
  pt: 'PT-BR',
  'pt-pt': 'PT-PT',
  en: 'EN',
  es: 'ES',
  ja: '日本',
  ru: 'RU',
};

export function LanguageSwitcher({
  variant = 'dropdown',
  showFlags = true,
  className = '',
}: LanguageSwitcherProps) {
  const { currentLanguage, options, changeLanguage } = useLanguageSwitcher();

  if (variant === 'minimal') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {options.map(option => (
          <button
            key={option.code}
            onClick={() => changeLanguage(option.code)}
            className={`px-2 py-1 text-sm rounded transition-colors ${
              option.active
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 dark:text-white hover:bg-gray-100'
            }`}
            title={option.nativeName}
          >
            {showFlags ? LANGUAGE_FLAGS[option.code] : LANGUAGE_DISPLAY[option.code]}
          </button>
        ))}
      </div>
    );
  }

  if (variant === 'flags') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {options.map(option => (
          <button
            key={option.code}
            onClick={() => changeLanguage(option.code)}
            className={`w-8 h-8 flex items-center justify-center rounded-full transition-all ${
              option.active
                ? 'ring-2 ring-blue-500 scale-110'
                : 'hover:scale-105 opacity-70 hover:opacity-100'
            }`}
            title={option.nativeName}
          >
            <span className='text-lg'>{LANGUAGE_FLAGS[option.code]}</span>
          </button>
        ))}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className={`relative ${className}`}>
      <select
        value={currentLanguage}
        onChange={e => changeLanguage(e.target.value)}
        className='appearance-none bg-white border border-gray-300 rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
      >
        {options.map(option => (
          <option key={option.code} value={option.code}>
            {showFlags ? `${LANGUAGE_FLAGS[option.code]} ` : ''}
            {option.nativeName}
          </option>
        ))}
      </select>
      <div className='absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none'>
        <svg
          className='w-4 h-4 text-gray-400'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </div>
    </div>
  );
}

// Advanced language switcher with currency info
export function AdvancedLanguageSwitcher({ className = '' }: { className?: string }) {
  const { currentLanguage, options, changeLanguage } = useLanguageSwitcher();
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className='flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
      >
        <span className='text-lg'>{LANGUAGE_FLAGS[currentLanguage]}</span>
        <span className='font-medium'>{LANGUAGE_DISPLAY[currentLanguage]}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className='fixed inset-0 z-10' onClick={() => setIsOpen(false)} />
          <div className='absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20'>
            <div className='py-1'>
              {options.map(option => (
                <button
                  key={option.code}
                  onClick={() => {
                    changeLanguage(option.code);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-gray-50 ${
                    option.active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <span className='text-lg'>{LANGUAGE_FLAGS[option.code]}</span>
                  <div className='flex-1'>
                    <div className='font-medium'>{option.nativeName}</div>
                    <div className='text-xs text-gray-500 dark:text-white dark:text-gray-400'>
                      {option.name}
                    </div>
                  </div>
                  {option.active && (
                    <svg className='w-4 h-4 text-blue-600' fill='currentColor' viewBox='0 0 20 20'>
                      <path
                        fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
